import React, { useMemo, useEffect, useState } from 'react';
// eslint-disable-next-line jira/restricted/use-debounce
import { useDebouncedCallback } from 'use-debounce';
import { fg } from '@atlassian/jira-feature-gating';
import type { Parent } from '@atlassian/jira-filters/src/common/types.tsx';
import ParentFilter from '@atlassian/jira-filters/src/ui/filters/parent/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { toIssueId, type IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { DEBOUNCE_MILLISECONDS, NO_EPIC_DISPLAYED_NAME } from '../../../common/constants.tsx';
import { useEpicIdsFilter } from '../../../controllers/top-filter/index.tsx';
import { useEpics } from '../../../services/get-epics/index.tsx';
import messages from './messages.tsx';

// ParentFilter expects IssueId and doesn't support an unassigned type
// so we transform NO_EPIC_DISPLAYED_NAME in and out of IssueId in this component
const EpicFilter = () => {
	// real-time input changes
	const [epicInputText, setEpicInputText] = useState('');
	// debounced input changes
	const [epicSearchText, setEpicSearchText] = useState('');
	const { data, error, loading } = useEpics(epicSearchText);
	const { formatMessage } = useIntl();
	const issueParents: Parent[] = useMemo(() => {
		if (error) {
			throw error;
		}
		if (!data) return [];
		const partialParents = data.map(({ id, summary }) => ({
			id: toIssueId(id),
			name: summary,
		}));

		return partialParents && partialParents.length && epicSearchText === ''
			? [
					{
						id: toIssueId(NO_EPIC_DISPLAYED_NAME),
						name: formatMessage(
							fg('jira-issue-terminology-update-m4')
								? messages.unassignedIssueTerminologyRefreshM4
								: messages.unassigned,
						),
					},
					...partialParents,
				]
			: partialParents;
	}, [data, epicSearchText, error, formatMessage]);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [selectedEpicIds, actions] = useEpicIdsFilter();
	const [searchEpic] = useDebouncedCallback(setEpicSearchText, DEBOUNCE_MILLISECONDS);
	useEffect(() => {
		searchEpic(epicInputText);
	}, [epicInputText, searchEpic]);

	return (
		<ParentFilter
			label="Epic"
			parents={issueParents}
			selectedParents={selectedEpicIds.map((selectedEpicId) => {
				if (selectedEpicId === null) {
					return toIssueId(NO_EPIC_DISPLAYED_NAME);
				}

				return toIssueId(selectedEpicId);
			})}
			inputValue={epicInputText}
			isLoading={loading}
			isAsyncSearch
			onChange={(selectedParents: IssueId[]) => {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'toggled',
						actionSubject: 'deploymentsEpicFilter',
					}),
				);

				actions.setEpicIdsFilter(
					selectedParents.map((id) => (id === toIssueId(NO_EPIC_DISPLAYED_NAME) ? null : id)),
				);
			}}
			onInputChange={(input, { action }) => {
				if (action === 'input-change') {
					setEpicInputText(input);
				}
			}}
			showMenuIfValuesAreEmpty
		/>
	);
};

export default EpicFilter;
